import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Communication, CommunicationTemplate, CommunicationChannel } from './types';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';
import { logError } from '@/hooks/useErrorHandler';

const isValidUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

export function useCommunications(candidateId: string) {
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const lastErrorAtRef = useRef<number>(0);

  const fetchCommunications = useCallback(async () => {
    // Guard against transient/empty candidateId states to avoid 400s and noisy toasts
    if (!candidateId || !isValidUuid(candidateId)) {
      setCommunications([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('communications')
        .select(`
          *,
          attachments:communication_attachments(*)
        `)
        .eq('candidate_id', candidateId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCommunications((data as unknown as Communication[]) || []);
    } catch (error: any) {
      console.error('Error fetching communications:', error);

      toast({
        title: 'Error',
        description: 'Failed to fetch communications',
        variant: 'destructive',
      });

      // Explicitly report handled errors (Sentry doesn't capture these automatically)
      const now = Date.now();
      const shouldReport = now - lastErrorAtRef.current > 30_000; // throttle to 1 per 30s
      if (shouldReport) {
        lastErrorAtRef.current = now;

        const err = error instanceof Error ? error : new Error(String(error?.message || error));
        captureError(err, {
          area: 'communications',
          action: 'fetch',
          candidateId,
          supabaseCode: error?.code,
          supabaseDetails: error?.details,
          supabaseHint: error?.hint,
        });

        void logError(
          'E_COMM_FETCH',
          err.message,
          {
            area: 'communications',
            action: 'fetch',
            candidateId,
            supabaseCode: error?.code,
            supabaseDetails: error?.details,
            supabaseHint: error?.hint,
          },
          'error'
        );
      }
    } finally {
      setLoading(false);
    }
  }, [candidateId, toast]);

  useEffect(() => {
    if (!candidateId || !isValidUuid(candidateId)) {
      setCommunications([]);
      setLoading(false);
      return;
    }

    fetchCommunications();

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`communications-${candidateId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'communications',
          filter: `candidate_id=eq.${candidateId}`,
        },
        () => {
          fetchCommunications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [candidateId, fetchCommunications]);

  const createCommunication = async (
    data: Partial<Communication>
  ): Promise<Communication | null> => {
    try {
      const { data: user } = await supabase.auth.getUser();
      
      // Get app_user id
      let createdBy = null;
      if (user.user) {
        const { data: appUser } = await supabase
          .from('app_users')
          .select('id')
          .eq('user_id', user.user.id)
          .single();
        createdBy = appUser?.id;
      }

      const { data: newComm, error } = await supabase
        .from('communications')
        .insert({
          candidate_id: candidateId,
          channel: data.channel,
          direction: data.direction || 'outbound',
          status: data.status || 'draft',
          subject: data.subject,
          body: data.body || '',
          recipient_email: data.recipient_email,
          recipient_phone: data.recipient_phone,
          job_id: data.job_id,
          created_by: createdBy,
        })
        .select()
        .single();

      if (error) throw error;
      return newComm as unknown as Communication;
    } catch (error) {
      console.error('Error creating communication:', error);
      toast({
        title: 'Error',
        description: 'Failed to create communication',
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateCommunication = async (
    id: string,
    data: Partial<Communication>
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('communications')
        .update({
          subject: data.subject,
          body: data.body,
          status: data.status,
          sent_at: data.sent_at,
        })
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating communication:', error);
      toast({
        title: 'Error',
        description: 'Failed to update communication',
        variant: 'destructive',
      });
      return false;
    }
  };

  const deleteCommunication = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('communications')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({
        title: 'Deleted',
        description: 'Communication deleted successfully',
      });
      return true;
    } catch (error) {
      console.error('Error deleting communication:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete communication',
        variant: 'destructive',
      });
      return false;
    }
  };

  const getStats = () => {
    const emails = communications.filter((c) => c.channel === 'email').length;
    const calls = communications.filter((c) => c.channel === 'phone').length;
    const whatsapp = communications.filter((c) => c.channel === 'whatsapp' || c.channel === 'sms').length;
    const notes = communications.filter((c) => c.channel === 'note').length;
    return { emails, calls, whatsapp, notes, total: communications.length };
  };

  return {
    communications,
    loading,
    fetchCommunications,
    createCommunication,
    updateCommunication,
    deleteCommunication,
    getStats,
  };
}

export function useTemplates(channel?: CommunicationChannel) {
  const [templates, setTemplates] = useState<CommunicationTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        let query = supabase
          .from('communication_templates')
          .select('*')
          .eq('is_active', true);

        if (channel) {
          query = query.eq('channel', channel);
        }

        const { data, error } = await query.order('name');
        if (error) throw error;
        setTemplates((data as unknown as CommunicationTemplate[]) || []);
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [channel]);

  return { templates, loading };
}
